import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCall,
  getCallByShareToken,
  listCalls,
} from "@/api/calls";
import { getCallConfig } from "@/api/call-config";
import { CallActionsMenu } from "@/components/calls/call-actions-menu";
import { CallInsightsPanel } from "@/components/calls/call-insights-panel";
import { CallScorecardEditor } from "@/components/calls/call-scorecard";
import { cn } from "@/cn";

/**
 * Apollo Conversations → Dialer: recordings, structured insights, Ask AI,
 * scorecards, and share links.
 */
export function ConversationsDialerPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const shareToken = params.get("share");
  const deepCallId = params.get("callId");
  const [selectedId, setSelectedId] = useState<string | null>(deepCallId);

  const listQuery = useQuery({
    queryKey: ["calls", "conversations-dialer"],
    queryFn: () => listCalls({ limit: 200 }),
  });

  const shareQuery = useQuery({
    queryKey: ["calls", "share", shareToken],
    queryFn: () => getCallByShareToken(shareToken!),
    enabled: Boolean(shareToken),
  });

  const deepQuery = useQuery({
    queryKey: ["calls", deepCallId],
    queryFn: () => getCall(deepCallId!),
    enabled: Boolean(deepCallId) && !shareToken,
  });

  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });

  useEffect(() => {
    if (shareQuery.data?.call) {
      setSelectedId(shareQuery.data.call.callId);
    } else if (deepQuery.data?.call) {
      setSelectedId(deepQuery.data.call.callId);
    } else if (deepCallId) {
      setSelectedId(deepCallId);
    }
  }, [shareQuery.data, deepQuery.data, deepCallId]);

  const calls = (listQuery.data?.calls ?? []).filter(
    (c) => c.recordingUrl || c.transcript || c.aiSummary || c.insights,
  );
  const selectedFromList = calls.find((c) => c.callId === selectedId);
  const selected =
    shareQuery.data?.call ??
    deepQuery.data?.call ??
    selectedFromList ??
    calls[0];

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Dialer conversations
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Recordings, Call Insights, Ask AI, and scorecards from the dialer.
          </p>
        </div>
        <Link to="/calls" className="text-sm text-brand underline">
          Back to Calls
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="rounded-lg border border-border">
          {calls.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              No dialer recordings yet.
            </p>
          )}
          <ul className="divide-y divide-border">
            {calls.map((c) => (
              <li key={c.callId}>
                <button
                  type="button"
                  className={cn(
                    "w-full px-3 py-2.5 text-left text-sm hover:bg-surface-hover",
                    selected?.callId === c.callId && "bg-surface-hover",
                  )}
                  onClick={() => setSelectedId(c.callId)}
                >
                  <div className="font-medium">
                    {c.leadName || c.toNumber || "Call"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {c.startedAt
                      ? new Date(c.startedAt).toLocaleString()
                      : c.createdAt}
                    {c.insights?.score != null
                      ? ` · score ${Math.round(c.insights.score)}`
                      : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-border p-4">
          {!selected && (
            <p className="text-sm text-muted-foreground">
              Select a recorded call.
            </p>
          )}
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-lg font-medium">
                    {selected.leadName || selected.toNumber}
                    {selected.interested ? (
                      <span className="ml-2 text-xs font-normal text-brand">
                        Interested
                      </span>
                    ) : null}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {selected.direction} · {selected.status}
                  </p>
                </div>
                <CallActionsMenu
                  call={selected}
                  onCallUpdated={() => {
                    void qc.invalidateQueries({ queryKey: ["calls"] });
                  }}
                />
              </div>
              {selected.recordingUrl && (
                <audio
                  controls
                  className="w-full"
                  src={selected.recordingUrl}
                />
              )}
              {selected.notes && (
                <section>
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                    Notes
                  </h3>
                  <p className="mt-1">{selected.notes}</p>
                </section>
              )}
              <CallInsightsPanel
                call={selected}
                onCallUpdated={() => {
                  void qc.invalidateQueries({ queryKey: ["calls"] });
                }}
              />
              <CallScorecardEditor
                call={selected}
                criteria={configQuery.data?.config.scorecardCriteria ?? []}
                onCallUpdated={() => {
                  void qc.invalidateQueries({ queryKey: ["calls"] });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
